import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type React from "react";
import { twJoin } from "tailwind-merge";

import iconMenu01 from "@/assets/icons/menu-01.svg";
import { IconButton } from "@/components/Button/IconButton";
import { Icon } from "@/components/Icon/Icon";
import { ArrowDownIcon } from "@/components/Icons/ArrowDownIcon";
import { ArrowUpIcon } from "@/components/Icons/ArrowUpIcon";

interface SurveyRespondQuestionCardDynamicRepeaterItemProps {
  id: string;
  title: string;
  description?: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: "up" | "down") => void;
  disabled?: boolean;
}

export function SurveyRespondQuestionCardDynamicRepeaterItem({
  id,
  title,
  description,
  canMoveUp,
  canMoveDown,
  onMove,
  disabled,
}: SurveyRespondQuestionCardDynamicRepeaterItemProps): React.ReactNode {
  const { attributes, listeners, isDragging, setNodeRef, transform, transition } = useSortable({
    id,
    disabled,
  });

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      className={twJoin(
        "relative flex items-center gap-4 rounded-lg border bg-white p-4",
        !isDragging ? "border-grey-300" : "border-transparent",
      )}
      ref={setNodeRef}
      style={style}
    >
      {isDragging && <div className="absolute left-0 top-0 z-10 size-full rounded-lg bg-blue-100" />}
      <div className="flex w-full items-center gap-4">
        <Icon
          name={iconMenu01}
          className={twJoin("text-grey-300", isDragging ? "cursor-grabbing" : "cursor-grab")}
          {...attributes}
          {...listeners}
        />
        <div className="flex flex-1 flex-col">
          <span className="text-headline3">{title}</span>
          {description && <p className="text-body text-grey-700">{description}</p>}
        </div>
        {(canMoveDown || canMoveUp) && (
          <div className="flex items-center gap-1">
            <IconButton
              title="Move up"
              styling="secondary"
              icon={<ArrowUpIcon />}
              disabled={!canMoveUp}
              size="sm"
              onClick={() => onMove("up")}
              isCircular
            />
            <IconButton
              title="Move down"
              styling="secondary"
              icon={<ArrowDownIcon />}
              disabled={!canMoveDown}
              size="sm"
              onClick={() => onMove("down")}
              isCircular
            />
          </div>
        )}
      </div>
    </div>
  );
}
